"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "../../lib/cva";
import type React from "react";
import type { Color } from "../../lib/colors";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";
import { Icon } from "./icon";

export const badgeVariants = cva(
  "relative inline-flex shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-sm border border-transparent font-medium outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-64 [&_svg:not([class*='opacity-'])]:opacity-80 [&_svg:not([class*='size-'])]:size-3 [&_svg]:pointer-events-none [&_svg]:shrink-0 [button&,a&]:cursor-pointer [button&,a&]:pointer-coarse:after:absolute [button&,a&]:pointer-coarse:after:top-1/2 [button&,a&]:pointer-coarse:after:left-1/2 [button&,a&]:pointer-coarse:after:-translate-x-1/2 [button&,a&]:pointer-coarse:after:-translate-y-1/2 [button&,a&]:pointer-coarse:after:size-full [button&,a&]:pointer-coarse:after:min-h-11 [button&,a&]:pointer-coarse:after:min-w-11",
  {
    defaultVariants: {
      size: "default",
      tone: "soft",
    },
    variants: {
      size: {
        default:
          "h-4.5 min-w-4.5 px-[calc(--spacing(1)-1px)] text-xs",
        lg: "h-5.5 min-w-5.5 px-[calc(--spacing(1.5)-1px)] text-sm",
        sm: "h-4 min-w-4 rounded-[.25rem] px-[calc(--spacing(1)-1px)] text-[.625rem]",
        // The labeled chip: a colored glyph + 12px label naming a group or
        // category (table group bands). Roomier than the count-badge sizes —
        // 24px tall with 8px insets and a 6px corner — and steady across
        // viewports: a band label doesn't downshift on desktop the way an
        // inline count does.
        chip: "h-6 min-w-6 rounded-md px-[calc(--spacing(2)-1px)] text-xs",
        // The value chip: a tag or a select/labels option riding a list cell,
        // a drawer property row, or a name column. Steady 20px in every
        // viewport — a value chip sits beside 20px+ row content and must not
        // downshift with the count badges. The 5px corner is the measured
        // reference value-chip radius (between the scale's 4px and 6px);
        // `pill` overrides it for the full-capsule tag family.
        // 14px REGULAR, not the count badges' 12px medium: the measured
        // reference value chip (20px tall, 14/16 text, weight regular).
        tag: "h-5 min-w-5 rounded-[0.3125rem] px-[calc(--spacing(1.5)-1px)] text-sm font-normal [&_svg:not([class*='size-'])]:size-3",
      },
      // The fill structure. The hue is a separate axis: `color` puts a name on
      // the [data-color] cascade and `soft`/`solid` read the wash and the
      // foreground off it, so one pair serves every name on the scale. Without
      // a color the cascade's colorless read applies — a subtle grey chip on
      // the secondary ink, not a bare box.
      tone: {
        // The wash plus the hue's INK rung — solved per hue and per mode, since
        // the fill tuned to carry white is too dim to read as text on its own
        // wash over a dark page. Names without an ink rung read the fill.
        soft: "bg-c-soft text-c-ink",
        // Full-strength fill with the on-color ink.
        solid: "bg-c text-c-foreground",
        // The hue as a tinted surface — deep on the dark page, pale on the
        // light one — with the hue's own ink. One categorical value reads as a
        // colored object without white-on-fill's shout, and every hue clears
        // the text floor in both modes because the ink is solved against this
        // surface. Names without a tint rung fall back to the soft wash.
        // Pressable (a button/anchor chip): a 2px ring in the chip's own tint
        // grows the surface on hover — the lift a filled chip can show without
        // a wash that its fill would hide.
        tonal:
          "bg-c-tint text-c-ink [button&,a&]:hover:ring-2 [button&,a&]:hover:ring-c-tint/60",
        // The hairline chip; the one tone that reads no color — border, fill
        // and ink are all fixed neutrals. The fill is the page surface in BOTH
        // modes, so the chip reads as an outline rather than a filled well: a
        // translucent input wash in dark mode made it a third thing, neither
        // outline nor soft. The ink is the secondary rung, because an outline
        // chip is the quiet one in a pile.
        outline:
          "border-input bg-background text-muted-foreground [button&,a&]:hover:bg-wash-hover",
      },
      /** Capsule corners, instead of the radius the size sets. */
      pill: { true: "rounded-full" },
    },
  },
);

export type BadgeProps = useRender.ComponentProps<"span"> &
  Omittable<{
  size?: VariantProps<typeof badgeVariants>["size"];
  /**
   * A color NAME — semantic (`success`, `destructive`, `secondary`, …) or
   * palette (an entity's stored hue). Travels as `data-color`; the tone reads
   * its wash and inks off the cascade. Omitted, the chip is the quiet
   * colorless one — a subtle grey on the secondary ink.
   */
  color?: Color;
  /**
   * Fill structure: the soft wash with colored text (default); `tonal` — the
   * hue as a deep (dark) or pale (light) surface under the hue's own ink, for
   * a single categorical value that should read heavier than a soft chip pile
   * without turning into a tile; `solid` — the full-strength fill with the
   * on-color ink (counts, status pips, tiles); or `outline`, the hairline chip
   * on the ambient fill. `outline` is the one tone `color` does not reach: its
   * ink and border are fixed neutrals.
   */
  tone?: VariantProps<typeof badgeVariants>["tone"];
  /** Capsule corners, instead of the radius the size sets. */
  pill?: VariantProps<typeof badgeVariants>["pill"];
  /**
   * A trailing × that removes the chip's subject (a token in a picker's search
   * well, a selected entity in a chip strip). Its own press target, separate
   * from anything the chip body does, so the two never collide. Wire
   * `dismissLabel` to name what is removed.
   */
  onDismiss?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** The ×'s accessible name — "Remove Water", not "Remove". */
  dismissLabel?: string;
}>;

export function Badge({
  className,
  size,
  color,
  tone,
  pill,
  render,
  onDismiss,
  dismissLabel,
  children,
  ...props
}: BadgeProps): React.ReactElement {
  const defaultProps = {
    className: cn(badgeVariants({ pill, size, tone }), className),
    "data-color": color,
    "data-slot": "badge",
    children: onDismiss ? (
      <>
        {children}
        {/* Pulled 2px into the chip's inset so the glyph sits where the label's
            trailing pad would, and the chip does not grow a second gutter. The
            ink is the chip's own; the dip lifts on hover to say "press me". */}
        <button
          type="button"
          aria-label={dismissLabel ?? "Remove"}
          className="-me-0.5 inline-flex shrink-0 cursor-pointer items-center justify-center rounded-xs opacity-72 outline-none transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring"
          onClick={(event) => {
            event.stopPropagation();
            onDismiss(event);
          }}
        >
          <Icon name="x" size="xs" />
        </button>
      </>
    ) : (
      children
    ),
  };

  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(defaultProps, props),
    render,
  });
}
